/*  src/pages/Flashcards.jsx  */
import React, { useEffect, useState } from "react";
import "./Flashcards.css";

/* ───────────────────────── lectures list ───────────────────────────── */
const LECTURES = [
  ...Array.from({ length: 16 }, (_, i) => ({
    label: `Lecture ${i + 1}`,
    key: `L${i + 1}`,
    file: `/Lec-Questions/Lecture${i + 1}_questions.json`,
  })),
  {
    label: "Circuit Flashcards",
    key: "CIRCUIT",
    file: "/Lec-Questions/circuit_flashcards.json",
  },
];

/* ───────────── local-storage helpers (persist stats) ──────────────── */
const keyFor = (lec, id) => `fc_${lec}_${id}`;
const loadStats = (lec, id) =>
  JSON.parse(
    localStorage.getItem(keyFor(lec, id)) || `{"attempts":0,"correct":0}`
  );
const saveStats = (lec, id, s) =>
  localStorage.setItem(keyFor(lec, id), JSON.stringify(s));
const clearStats = (lec) =>
  Object.keys(localStorage)
    .filter((k) => k.startsWith(`fc_${lec}_`))
    .forEach((k) => localStorage.removeItem(k));

/* ─────────── slide-image resolver ──────────── */
const slidesCtx = require.context(
  "../../src/assets/circuit_slides_png",
  false, // ❌ no nested dirs
  /\.(png|jpe?g|svg)$/i
);

export const imgSrc = (path = "") => {
  /* 1. full URLs (http/https/data) */
  if (/^(https?:|data:)/i.test(path)) return path;

  /* 2. `/public/*` absolute paths keep working */
  if (path.startsWith("/")) {
    // special-case “…/circuit_slides_pngs/…” → first try bundled img
    const trimmed = path.replace(/^\/+/, "");
    if (/^circuit_slides_pngs?\//i.test(trimmed)) {
      try {
        return slidesCtx(`./${trimmed.replace(/^circuit_slides_pngs?\//, "")}`);
      } catch {
        /* ignore – fall back to /public below */
      }
    }
    return `${process.env.PUBLIC_URL}/${trimmed}`;
  }

  /* 3. plain file name OR `circuit_slides_png/…` */
  try {
    const key = `./${path.replace(/^circuit_slides_pngs?\//, "")}`;
    return slidesCtx(key);
  } catch (err) {
    console.warn("imgSrc: slide not bundled:", path, err);
    return `${process.env.PUBLIC_URL}/${path.replace(/^\/+/, "")}`;
  }
};

/* ─────────────────────────── component ───────────────────────────── */
export default function Flashcards() {
  const [lecKey, setLecKey] = useState(LECTURES[0].key);
  const [cards, setCards] = useState([]);
  const [cur, setCur] = useState(null);
  const [pick, setPick] = useState("");
  const [fb, setFb] = useState("");

  /* ── fetch whenever lecture changes ─────────────────────────────── */
  useEffect(() => {
    const { file } = LECTURES.find((l) => l.key === lecKey);
    fetch(file)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      })
      .then((raw) =>
        normalise(raw).map((c) => ({ ...c, stats: loadStats(lecKey, c.id) }))
      )
      .then((deck) => {
        setCards(deck);
        setCur(draw(deck));
        setPick("");
        setFb("");
      })
      .catch((e) => alert(`Couldn't load ${file}: ${e.message}`));
  }, [lecKey]);

  /* ── weighted roulette pick (1/(correct+1)) ─────────────────────── */
  const draw = (deck) => {
    if (!deck.length) return null;
    const w = deck.map(({ stats }) => 1 / (stats.correct + 1));
    const total = w.reduce((a, b) => a + b, 0);
    for (let r = Math.random() * total, i = 0; ; i++)
      if ((r -= w[i]) <= 0) return deck[i];
  };

  /* ── submit / next / reset ───────────────────────────────────────── */
  const submit = () => {
    if (!pick || !cur || fb) return;
    const ok = pick === cur.answer;
    const ns = {
      attempts: cur.stats.attempts + 1,
      correct: cur.stats.correct + (ok ? 1 : 0),
    };
    saveStats(lecKey, cur.id, ns);
    setCards((p) => p.map((c) => (c.id === cur.id ? { ...c, stats: ns } : c)));
    setCur((c) => ({ ...c, stats: ns }));
    setFb(ok ? "✅ Correct!" : `❌ Incorrect. Answer: ${cur.answer}`);
  };
  const next = () => {
    setCur(draw(cards));
    setPick("");
    setFb("");
  };
  const reset = () => {
    if (window.confirm(`Reset progress for ${lecKey}?`)) {
      clearStats(lecKey);
      setCards((p) =>
        p.map((c) => ({ ...c, stats: { attempts: 0, correct: 0 } }))
      );
      setCur((c) => c && { ...c, stats: { attempts: 0, correct: 0 } });
      setFb("");
      setPick("");
    }
  };

  /* ── render ───────────────────────────────────────────────────────── */
  if (!cur) return <p className="loading">Loading…</p>;
  const { attempts, correct } = cur.stats,
    isCircuit = cur.isCircuit,
    showImg = fb && cur.image;

  return (
    <div className="Flashcards">
      {/* top-bar */}
      <div className="lecture-bar">
        <select value={lecKey} onChange={(e) => setLecKey(e.target.value)}>
          {LECTURES.map((l) => (
            <option key={l.key} value={l.key}>
              {l.label}
            </option>
          ))}
        </select>
        <button className="reset" onClick={reset}>
          Clear stats
        </button>
      </div>

      {/* card */}
      <div className="card">
        <h3>
          {cur.id}. {cur.question}
        </h3>

        {isCircuit && (
          <p className="components">
            <em>{cur.components}</em>
          </p>
        )}

        {showImg && (
          <img
            className="card-img"
            src={imgSrc(cur.image)}
            alt="flash illustration"
          />
        )}

        <form>
          {cur.options.map((opt, i) => (
            <label key={opt} className="option">
              <input
                type="radio"
                checked={pick === opt}
                onChange={() => setPick(opt)}
                disabled={!!fb}
              />
              <span className="letter">{String.fromCharCode(97 + i)})</span>{" "}
              {opt}
            </label>
          ))}
        </form>

        <button className="submit" onClick={submit} disabled={!pick || fb}>
          {isCircuit ? "Reveal" : "Submit"}
        </button>

        <p className="progress">
          Seen&nbsp;{attempts}× | Correct&nbsp;{correct}
        </p>

        {fb && (
          <>
            <p className="feedback">
              {fb}
              {cur.expl && (
                <>
                  <br />
                  <em>{cur.expl}</em>
                </>
              )}
            </p>
            <button className="next" onClick={next}>
              Next question
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ────────── normaliser ─────────────────────────────────────────────── */
function normalise(raw) {
  /* MCQ → has “question”; circuit deck → “front/back” */
  if (raw[0]?.question !== undefined) {
    return raw.map((c) => ({
      ...c,
      expl: c["why/how"] ?? "",
      isCircuit: false,
    }));
  }
  return raw.map((c, i) => {
    const [componentsLine] = c.back.split("\n");
    return {
      id: i + 1,
      question: c.front,
      components: componentsLine,
      options: ["Show answer"],
      answer: "Show answer",
      expl: c.back,
      image: c.image,
      isCircuit: true,
    };
  });
}
